"use client"

import { useState } from "react"
import Image from "next/image"
import { X, ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"

type Category = "todos" | "reformas" | "construcoes" | "piscinas"

interface PortfolioProps {
  dict: {
    label: string
    title: string
    description: string
    filters: {
      all: string
      renovations: string
      constructions: string
      pools: string
    }
    lightbox: {
      close: string
      prev: string
      next: string
      aria_label: string
    }
  }
}

const PROJECTS = [
  {
    src: "/images/portfolio-1.jpg",
    alt: "Sala de estar reformada com vista para o mar",
    title: "Reforma Completa — Geribá",
    category: "reformas" as const,
  },
  {
    src: "/images/portfolio-2.jpg",
    alt: "Piscina de borda infinita com vista oceânica",
    title: "Piscina Infinita — Orla Bardot",
    category: "piscinas" as const,
  },
  {
    src: "/images/portfolio-3.jpg",
    alt: "Cozinha moderna reformada em casa de praia",
    title: "Cozinha Gourmet — Ferradura",
    category: "reformas" as const,
  },
  {
    src: "/images/portfolio-4.jpg",
    alt: "Fachada de casa nova construída em Búzios",
    title: "Construção Residencial — Manguinhos",
    category: "construcoes" as const,
  },
  {
    src: "/images/portfolio-5.jpg",
    alt: "Área de churrasqueira e lazer ao ar livre",
    title: "Área Gourmet — João Fernandes",
    category: "piscinas" as const,
  },
  {
    src: "/images/portfolio-6.jpg",
    alt: "Banheiro master com acabamento de mármore",
    title: "Suíte Master — Praia Brava",
    category: "reformas" as const,
  },
]

export function Portfolio({ dict }: PortfolioProps) {
  const [activeFilter, setActiveFilter] = useState<Category>("todos")
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const filters: { label: string; value: Category }[] = [
    { label: dict.filters.all, value: "todos" },
    { label: dict.filters.renovations, value: "reformas" },
    { label: dict.filters.constructions, value: "construcoes" },
    { label: dict.filters.pools, value: "piscinas" },
  ]

  const filtered =
    activeFilter === "todos"
      ? PROJECTS
      : PROJECTS.filter((p) => p.category === activeFilter)

  const openLightbox = (idx: number) => setLightboxIndex(idx)
  const closeLightbox = () => setLightboxIndex(null)

  const goNext = () => {
    if (lightboxIndex === null) return
    setLightboxIndex((lightboxIndex + 1) % filtered.length)
  }

  const goPrev = () => {
    if (lightboxIndex === null) return
    setLightboxIndex((lightboxIndex - 1 + filtered.length) % filtered.length)
  }

  return (
    <section id="portfolio" className="bg-card py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        {/* Section header */}
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-primary">
            {dict.label}
          </p>
          <h2 className="mt-3 font-serif text-3xl font-black tracking-tight text-foreground md:text-4xl lg:text-5xl text-balance">
            {dict.title}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground text-pretty">
            {dict.description}
          </p>
        </div>

        {/* Filter buttons */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
          {filters.map((f) => (
            <Button
              key={f.value}
              variant={activeFilter === f.value ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveFilter(f.value)}
              className={
                activeFilter === f.value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground"
              }
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((project, idx) => (
            <button
              key={project.src}
              onClick={() => openLightbox(idx)}
              className="group relative aspect-[4/3] overflow-hidden rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Image
                src={project.src}
                alt={project.alt}
                fill
                className="object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              />
              <div className="absolute inset-0 bg-foreground/0 transition-all duration-300 group-hover:bg-foreground/40" />
              <div className="absolute inset-x-0 bottom-0 translate-y-full p-4 transition-transform duration-300 group-hover:translate-y-0">
                <p className="text-sm font-bold text-primary-foreground">
                  {project.title}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/90 backdrop-blur-sm"
          onClick={closeLightbox}
          role="dialog"
          aria-label={dict.lightbox.aria_label}
        >
          <button
            onClick={closeLightbox}
            className="absolute right-4 top-4 rounded-full bg-card/20 p-2 text-primary-foreground transition-colors hover:bg-card/40"
            aria-label={dict.lightbox.close}
          >
            <X className="h-6 w-6" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation()
              goPrev()
            }}
            className="absolute left-4 rounded-full bg-card/20 p-2 text-primary-foreground transition-colors hover:bg-card/40"
            aria-label={dict.lightbox.prev}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <div
            className="relative h-[80vh] w-[90vw] max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <Image
              src={filtered[lightboxIndex].src}
              alt={filtered[lightboxIndex].alt}
              fill
              className="rounded-xl object-contain"
              sizes="90vw"
            />
            <div className="absolute inset-x-0 bottom-0 rounded-b-xl bg-foreground/60 px-6 py-4 backdrop-blur-sm">
              <p className="text-base font-bold text-primary-foreground">
                {filtered[lightboxIndex].title}
              </p>
            </div>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation()
              goNext()
            }}
            className="absolute right-4 rounded-full bg-card/20 p-2 text-primary-foreground transition-colors hover:bg-card/40"
            aria-label={dict.lightbox.next}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      )}
    </section>
  )
}
